import type { Dayjs } from "dayjs"
import type {
  AdBlastReportCompany,
  AdBlastReportLine,
  AdLaunchGoal,
} from "~/ui-rtk/api/adBlastReportsApi"

/**
 * Pure computation behind the "Aggregate" sheet of the Ad Blast Reports page —
 * the numbers the agency's manual "Ads Launch Tracker - Aggregate" tab shows,
 * derived from launch-tracker lines instead of hand-filled rows.
 *
 * Definitions (mirrored in the sheet's "Definitions" block):
 *  - Launched: one ad created by a blast into one ad set (QA is not "Failed").
 *  - Live:     a launched ad whose current status is Live.
 *  - Windows are relative to "as of" (the range end, capped at now), in the
 *    viewer's timezone; weeks are ISO weeks (Mon–Sun).
 *  - Daily goal: the weekly low goal spread over the five weekdays
 *    (ceil(low / 5) Mon–Fri, 0 on weekends).
 */

/** dayjs factory — the page passes `useDate()`, tests pass dayjs with isoWeek loaded. */
export type DayjsFactory = (date?: string | number | Date | Dayjs) => Dayjs

export interface DateWindow {
  key: "yesterday" | "today" | "thisWeek" | "lastWeek" | "range"
  label: string
  start: Date
  end: Date
}

export interface WindowCounts {
  launched: number
  live: number
}

export interface CustomerGoalStatus {
  companyId: string
  companyName: string
  goalLow: number
  goalHigh: number
  hasGoal: boolean
  yesterday: WindowCounts
  today: WindowCounts
  thisWeek: WindowCounts
  lastWeek: WindowCounts
  range: WindowCounts
  /** thisWeek.live / goalLow, as a 0–1 ratio (null when no goal). */
  attainment: number | null
  /** "on goal" | "over goal" | "short by N" | "no goal". */
  goalStatus: string
  /** Signed distance from the low goal (negative = short). */
  delta: number
  channelsThisWeek: string[]
  latestLaunchAt: string | null
}

export interface CustomerChannelRow {
  companyId: string
  companyName: string
  channel: string
  launched: number
  live: number
}

export interface AggregateSnapshot {
  yesterdayGoal: number
  yesterdayLive: number
  todayGoal: number
  todayLive: number
  thisWeekLaunched: number
  thisWeekLive: number
  weeklyGoal: number
  /** 0–1 ratio; null when nothing is tracked. */
  weeklyAttainment: number | null
  customersOnGoal: number
  customersTracked: number
  lastWeekLive: number
  lastWeekGoal: number
  lastWeekAttainment: number | null
  lastWeekCustomersOnGoal: number
}

export interface Aggregate {
  asOf: Date
  windows: DateWindow[]
  windowCounts: Record<DateWindow["key"], WindowCounts>
  snapshot: AggregateSnapshot
  customers: CustomerGoalStatus[]
  needsAttention: CustomerGoalStatus[]
  overGoal: CustomerGoalStatus[]
  byCustomerChannel: CustomerChannelRow[]
  /** Lines inside the selected range, newest first. */
  recentLaunches: AdBlastReportLine[]
}

export interface AggregateInput {
  dayjs: DayjsFactory
  /** Every fetched line (the fetch window is widened to cover last week). */
  lines: AdBlastReportLine[]
  companies: AdBlastReportCompany[]
  goals: Record<string, AdLaunchGoal | undefined>
  /** The user-selected range. */
  range: { from: Date; to: Date }
  /** "Now" for the snapshot windows — the range end capped at the real now. */
  asOf: Date
}

export const isLaunched = (line: AdBlastReportLine): boolean =>
  line.adsCreated > 0 && line.qa !== "Failed"

export const isLive = (line: AdBlastReportLine): boolean =>
  isLaunched(line) && /^live/i.test(line.status)

/** Weekly low goal spread over Mon–Fri; weekends carry no daily goal. */
export function dailyGoal(goalLow: number, day: Dayjs): number {
  const isoWeekday = day.isoWeekday()
  if (isoWeekday > 5 || goalLow <= 0) return 0
  return Math.ceil(goalLow / 5)
}

export function buildWindows(
  dayjs: DayjsFactory,
  asOf: Date,
  range: { from: Date; to: Date },
): DateWindow[] {
  const now = dayjs(asOf)
  const thisWeekStart = now.startOf("isoWeek")
  return [
    {
      key: "yesterday",
      label: "Yesterday",
      start: now.subtract(1, "day").startOf("day").toDate(),
      end: now.subtract(1, "day").endOf("day").toDate(),
    },
    {
      key: "today",
      label: "Today so far",
      start: now.startOf("day").toDate(),
      end: now.toDate(),
    },
    {
      key: "thisWeek",
      label: "This week",
      start: thisWeekStart.toDate(),
      end: thisWeekStart.endOf("isoWeek").toDate(),
    },
    {
      key: "lastWeek",
      label: "Last week",
      start: thisWeekStart.subtract(1, "week").toDate(),
      end: thisWeekStart.subtract(1, "week").endOf("isoWeek").toDate(),
    },
    {
      key: "range",
      label: "Selected range",
      start: range.from,
      end: range.to,
    },
  ]
}

const inWindow = (line: AdBlastReportLine, window: DateWindow): boolean => {
  const at = Date.parse(line.launchedAt)
  return at >= window.start.getTime() && at <= window.end.getTime()
}

const count = (lines: AdBlastReportLine[]): WindowCounts => ({
  launched: lines.filter(isLaunched).length,
  live: lines.filter(isLive).length,
})

const ratio = (value: number, goal: number): number | null =>
  goal > 0 ? value / goal : null

function describeGoalStatus(
  hasGoal: boolean,
  live: number,
  goalHigh: number,
  delta: number,
): string {
  if (!hasGoal) return "no goal"
  if (live > goalHigh) return "over goal"
  if (delta >= 0) return "on goal"
  return `short by ${-delta}`
}

export function computeAggregate(input: AggregateInput): Aggregate {
  const { dayjs, lines, companies, goals, range, asOf } = input
  const windows = buildWindows(dayjs, asOf, range)
  const byKey = Object.fromEntries(windows.map(w => [w.key, w])) as Record<
    DateWindow["key"],
    DateWindow
  >

  const windowCounts = Object.fromEntries(
    windows.map(w => [w.key, count(lines.filter(l => inWindow(l, w)))]),
  ) as Record<DateWindow["key"], WindowCounts>

  const yesterday = dayjs(byKey.yesterday.start)
  const today = dayjs(byKey.today.start)

  const customers: CustomerGoalStatus[] = companies
    .map(company => {
      const own = lines.filter(l => l.companyId === company.id)
      const goal = goals[company.id]
      const goalLow = goal?.low ?? 0
      const goalHigh = goal?.high ?? 0
      const hasGoal = Boolean(goal)
      const thisWeekLines = own.filter(l => inWindow(l, byKey.thisWeek))
      const thisWeek = count(thisWeekLines)
      const delta = thisWeek.live - goalLow
      const goalStatus = describeGoalStatus(
        hasGoal,
        thisWeek.live,
        goalHigh,
        delta,
      )
      const latest = own.reduce<string | null>(
        (max, l) => (max === null || l.launchedAt > max ? l.launchedAt : max),
        null,
      )
      return {
        companyId: company.id,
        companyName: company.name,
        goalLow,
        goalHigh,
        hasGoal,
        yesterday: count(own.filter(l => inWindow(l, byKey.yesterday))),
        today: count(own.filter(l => inWindow(l, byKey.today))),
        thisWeek,
        lastWeek: count(own.filter(l => inWindow(l, byKey.lastWeek))),
        range: count(own.filter(l => inWindow(l, byKey.range))),
        attainment: hasGoal ? ratio(thisWeek.live, goalLow) : null,
        goalStatus,
        delta,
        channelsThisWeek: [
          ...new Set(
            thisWeekLines
              .filter(isLaunched)
              .flatMap(l => l.channel.split(",").map(c => c.trim()))
              .filter(Boolean),
          ),
        ].sort(),
        latestLaunchAt: latest,
      }
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName))

  const tracked = customers.filter(c => c.hasGoal)
  const weeklyGoal = tracked.reduce((sum, c) => sum + c.goalLow, 0)
  const onGoal = (c: CustomerGoalStatus, live: number) =>
    c.hasGoal && live >= c.goalLow

  const snapshot: AggregateSnapshot = {
    yesterdayGoal: tracked.reduce(
      (sum, c) => sum + dailyGoal(c.goalLow, yesterday),
      0,
    ),
    yesterdayLive: windowCounts.yesterday.live,
    todayGoal: tracked.reduce((sum, c) => sum + dailyGoal(c.goalLow, today), 0),
    todayLive: windowCounts.today.live,
    thisWeekLaunched: windowCounts.thisWeek.launched,
    thisWeekLive: windowCounts.thisWeek.live,
    weeklyGoal,
    weeklyAttainment: ratio(windowCounts.thisWeek.live, weeklyGoal),
    customersOnGoal: tracked.filter(c => onGoal(c, c.thisWeek.live)).length,
    customersTracked: tracked.length,
    lastWeekLive: windowCounts.lastWeek.live,
    lastWeekGoal: weeklyGoal,
    lastWeekAttainment: ratio(windowCounts.lastWeek.live, weeklyGoal),
    lastWeekCustomersOnGoal: tracked.filter(c => onGoal(c, c.lastWeek.live))
      .length,
  }

  const channelRows = new Map<string, CustomerChannelRow>()
  for (const line of lines.filter(l => inWindow(l, byKey.thisWeek))) {
    for (const channel of line.channel.split(",").map(c => c.trim())) {
      const key = `${line.companyId}|${channel || "—"}`
      const row = channelRows.get(key) ?? {
        companyId: line.companyId,
        companyName: line.companyName,
        channel: channel || "—",
        launched: 0,
        live: 0,
      }
      if (isLaunched(line)) row.launched += 1
      if (isLive(line)) row.live += 1
      channelRows.set(key, row)
    }
  }

  return {
    asOf,
    windows,
    windowCounts,
    snapshot,
    customers,
    needsAttention: tracked.filter(c => c.delta < 0),
    overGoal: tracked.filter(c => c.goalStatus === "over goal"),
    byCustomerChannel: [...channelRows.values()].sort(
      (a, b) =>
        a.companyName.localeCompare(b.companyName) ||
        a.channel.localeCompare(b.channel),
    ),
    recentLaunches: lines
      .filter(l => inWindow(l, byKey.range))
      .sort((a, b) => b.launchedAt.localeCompare(a.launchedAt)),
  }
}

export interface ComparisonRow {
  companyId: string
  companyName: string
  current: WindowCounts
  previous: WindowCounts
  /** current − previous */
  deltaLaunched: number
  deltaLive: number
}

export interface Comparison {
  rows: ComparisonRow[]
  totals: ComparisonRow
}

/**
 * Selected range vs the picker's comparison period, per customer plus a
 * totals row. Both line sets must already be filtered to their own range.
 */
export function computeComparison(
  companies: AdBlastReportCompany[],
  currentLines: AdBlastReportLine[],
  previousLines: AdBlastReportLine[],
): Comparison {
  const row = (
    companyId: string,
    companyName: string,
    current: AdBlastReportLine[],
    previous: AdBlastReportLine[],
  ): ComparisonRow => {
    const cur = count(current)
    const prev = count(previous)
    return {
      companyId,
      companyName,
      current: cur,
      previous: prev,
      deltaLaunched: cur.launched - prev.launched,
      deltaLive: cur.live - prev.live,
    }
  }
  const ids = new Map(companies.map(c => [c.id, c.name]))
  for (const line of [...currentLines, ...previousLines]) {
    if (!ids.has(line.companyId)) ids.set(line.companyId, line.companyName)
  }
  const rows = [...ids.entries()]
    .map(([id, name]) =>
      row(
        id,
        name,
        currentLines.filter(l => l.companyId === id),
        previousLines.filter(l => l.companyId === id),
      ),
    )
    .filter(r => r.current.launched + r.previous.launched > 0)
    .sort((a, b) => a.companyName.localeCompare(b.companyName))
  return { rows, totals: row("__total", "Total", currentLines, previousLines) }
}

/** "+3" / "−2" / "0" for delta columns. */
export function formatDelta(value: number): string {
  if (value > 0) return `+${value}`
  if (value < 0) return `−${-value}`
  return "0"
}

/** "94.5%" / "0.00%" style, matching the sheet. */
export const formatPercent = (value: number | null): string =>
  value === null ? "—" : `${(value * 100).toFixed(2)}%`
